import React, { useState, useEffect, FormEvent, ChangeEvent, useRef } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useTranslation } from '../../../context/TranslationContext';
import { createProduct, updateProduct, uploadImage } from '../../../services/api';
import { Product } from '../../../types';
import './ProductModal.css';

interface ProductModalProps {
  product: Product | null;
  onClose: (productUpdated?: boolean) => void;
}

const ProductModal: React.FC<ProductModalProps> = ({ product, onClose }) => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [formData, setFormData] = useState<Omit<Product, '_id'>>({
    name: '',
    description: '',
    price: 0,
    imageUrl: '',
    available: true
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadingImage, setUploadingImage] = useState<boolean>(false);
  const [uploadSuccess, setUploadSuccess] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Initialize form data if editing an existing product
  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name,
        description: product.description,
        price: product.price,
        imageUrl: product.imageUrl || '',
        available: product.available
      });
    }
  }, [product]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const target = e.target as HTMLInputElement;
      setFormData(prev => ({
        ...prev,
        [name]: target.checked
      }));
    } else if (name === 'price') {
      // Handle price as a number
      setFormData(prev => ({
        ...prev,
        [name]: parseFloat(value) || 0
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
      setUploadSuccess(false); // Reset upload success state
    }
  };

  const handleImageUpload = async () => {
    if (!selectedFile || !token) return;
    
    try {
      setUploadingImage(true);
      setError(null);
      
      // Upload the new image without deleting the old one
      const result = await uploadImage(selectedFile, token);
      
      // Update form data with the new image URL
      setFormData(prev => ({
        ...prev,
        imageUrl: result.imageUrl
      }));
      
      setUploadSuccess(true);
      setSelectedFile(null);
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      setError(t('admin.products.imageUploadError'));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!token) return;
    
    try {
      setLoading(true);
      setError(null);
      
      if (product) {
        // Get product ID (either _id or id)
        const productId = product._id || (product as any).id?.toString();
        
        // Validate product ID
        if (!productId) {
          throw new Error('Product ID is missing');
        }
        
        console.log('ProductModal: Updating product with ID:', productId);
        
        // Update existing product
        await updateProduct(productId, formData, token);
      } else {
        // Create new product
        await createProduct(formData, token);
      }
      
      onClose(true); // Close modal and refresh product list
    } catch (err) {
      console.error('Error saving product:', err);
      setError(t('admin.common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-header">
          <h2>{product ? t('admin.products.editProduct') : t('admin.products.addProduct')}</h2>
          <button className="modal-close-button" onClick={() => onClose()}>×</button>
        </div>
        
        <div className="modal-content">
          {error && <div className="error-message">{error}</div>}
          
          <form onSubmit={handleSubmit} className="product-form">
            <div className="form-group">
              <label htmlFor="name">{t('admin.products.productName')}</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="description">{t('admin.products.description')}</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="form-control"
                rows={4}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="price">{t('admin.products.price')}</label>
              <input
                type="number"
                id="price"
                name="price"
                value={formData.price}
                onChange={handleChange}
                className="form-control"
                min="0"
                step="1"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="imageUrl">{t('admin.products.imageUrl')}</label>
              <div className="image-upload-container">
                <input
                  type="text"
                  id="imageUrl"
                  name="imageUrl"
                  value={formData.imageUrl}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="https://example.com/image.jpg"
                  readOnly={uploadingImage}
                />
                
                <div className="image-upload-controls">
                  <input
                    type="file"
                    id="image"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept="image/*"
                    className="file-input"
                    disabled={uploadingImage}
                  />
                  <label htmlFor="image" className="file-input-label">
                    {t('admin.products.chooseImage')}
                  </label>
                  
                  <button
                    type="button"
                    onClick={handleImageUpload}
                    className="upload-button"
                    disabled={!selectedFile || uploadingImage}
                  >
                    {uploadingImage 
                      ? t('admin.products.uploadingImage')
                      : t('admin.products.uploadImage')
                    }
                  </button>
                </div>
                
                {uploadSuccess && (
                  <div className="upload-success-message">
                    {t('admin.products.imageUploaded')}
                  </div>
                )}
                
                {selectedFile && (
                  <div className="selected-file">
                    {selectedFile.name} ({Math.round(selectedFile.size / 1024)} KB)
                  </div>
                )}
              </div>
              
              {formData.imageUrl && (
                <div className="image-preview">
                  <img src={formData.imageUrl} alt={t('admin.products.imagePreview')} />
                </div>
              )}
            </div>
            
            <div className="form-group checkbox-group">
              <input
                type="checkbox"
                id="available"
                name="available"
                checked={formData.available}
                onChange={handleChange}
              />
              <label htmlFor="available">{t('admin.products.available')}</label>
            </div>
            
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => onClose()}
              >
                {t('admin.products.cancel')}
              </button>
              <button 
                type="submit" 
                className="btn btn-primary" 
                disabled={loading}
              >
                {loading ? t('admin.common.loading') : t('admin.products.save')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProductModal;
